/*
 * Banyomda recommendation loader
 * - Uses Shopify's native RELATED / COMPLEMENTARY intents.
 * - Preserves Shopify recommendation URLs so recommendation conversion tracking works.
 * - Guards against duplicate initialization and stale DOM updates.
 * - Hides empty recommendation sections without deleting their parent section.
 */
if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();
        this.abortController = null;
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.dataset.state = 'loading';

        const start = () => {
          if (!this.isConnected) return;
          this.init();
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(start, { timeout: 1500 });
        } else if (window.FoxTheme?.Motion?.inView) {
          FoxTheme.Motion.inView(this, start, { margin: '0px 0px 400px 0px' });
        } else {
          window.setTimeout(start, 0);
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
      }

      async init() {
        if (!this.isConnected || this.dataset.loading === 'true') return;
        this.dataset.loading = 'true';
        this.abortController = new AbortController();

        try {
          const response = await fetch(this.dataset.url, {
            signal: this.abortController.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

          const responseText = await response.text();
          if (!this.isConnected) return;

          const sectionInnerHTML = new DOMParser()
            .parseFromString(responseText, 'text/html')
            .querySelector('.shopify-section');

          if (!sectionInnerHTML) {
            this.setEmptyState();
            return;
          }

          const recommendations = sectionInnerHTML.querySelector('product-recommendations');
          if (!recommendations) {
            this.setEmptyState();
            return;
          }

          const hasProducts = recommendations.querySelector('.products-grid .f-column, .products-grid .swiper-slide, .product-card, .byd-grid-card, .newbanyomda-editorial-product-card');
          if (!hasProducts || !recommendations.innerHTML.trim()) {
            this.setEmptyState();
            return;
          }

          this.innerHTML = recommendations.innerHTML;
          this.dataset.state = 'loaded';
          this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
        } catch (error) {
          if (error?.name === 'AbortError') return;
          console.warn('[Banyomda] Recommendation request unavailable.', error);
          this.setEmptyState();
        } finally {
          this.dataset.loading = 'false';
          this.abortController = null;
        }
      }

      setEmptyState() {
        this.dataset.state = 'empty';
        const parentSection = this.closest('.shopify-section');
        if (parentSection) parentSection.hidden = true;
        this.dispatchEvent(new CustomEvent('recommendations:empty', { bubbles: true }));
      }
    }
  );
}
